import { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, Input, Modal, Select, Space, Tag, Typography, message } from 'antd';
import {
  ExclamationCircleOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import {
  ErpDataGrid,
  ErpMasterShell,
  useErpMasterQuery
} from '../../../../components/common/erp-grid';
import useApprovalMfa from '../../../../hooks/useApprovalMfa';
import { buildPaymentColumns } from './paymentVoucherColumns';
import { paymentVoucherApi } from './paymentVoucherApi';
import {
  PAYMENT_TYPE_LABEL, PAYMENT_TYPES
} from './paymentVoucherConstants';
import PaymentVoucherModal from './PaymentVoucherModal';
// Stage 6B.9F — period guard for the Post action.
import { accountsApi } from '../accountsApi';
import {
  classifyPeriodForDate, periodGuardError, loadPeriodsForGuard
} from '../accountingPeriodGuard';
import '../accounts.css';

const { Title } = Typography;

// ─────────────────────────────────────────────────────────────────
// Glass-i — Payment / Receipt Voucher page (Stage 6B.6).
//
// Single grid hosts BOTH supplier payments and customer receipts
// (filtered by payment_type at the toolbar). The modal handles
// both flows; the parent page owns Post / Cancel so the
// Approval-MFA modal doesn't visually stack on the form modal.
//
// MFA op key is resolved server-side based on payment_type so the
// audit trail + MFA dialog title pick the right wording (Payment
// vs Receipt) automatically.
// ─────────────────────────────────────────────────────────────────

function defaultOnError(err) {
  const msg = err?.response?.data?.error?.message ||
    err?.message || 'Failed to load payments.';
  message.error({ content: msg, key: 'payment-list' });
}

export default function PaymentVoucherPage() {
  const [paymentTypeFilter, setPaymentTypeFilter] = useState('all');

  // Memoise the adapter so its reference is stable across renders.
  // An inline adapter would change on every render → `fetchOnce` in
  // useErpMasterQuery would recompute → its auto-fetch useEffect would
  // re-fire → infinite request loop. The adapter only needs to change
  // when the payment-type filter changes, since the filter is the only
  // value captured by closure inside `list`.
  const adapter = useMemo(() => ({
    list: (q) => {
      const extra = paymentTypeFilter === 'all'
        ? null
        : { paymentType: paymentTypeFilter };
      return paymentVoucherApi.list({ ...(q || {}), ...(extra || {}) });
    }
  }), [paymentTypeFilter]);

  const { gridHandlers, refetch, query } = useErpMasterQuery({
    adapter,
    pageSize: 25,
    onError: defaultOnError
  });
  const currentPage = query && query.page     ? query.page     : 1;
  const pageSize    = query && query.pageSize ? query.pageSize : 25;
  const columns = useMemo(
    () => buildPaymentColumns({ currentPage, pageSize }),
    [currentPage, pageSize]);

  const { runWithApproval, ApprovalMfaModalSlot } = useApprovalMfa();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState('add');
  const [drawerRecord, setDrawerRecord] = useState(null);

  // Stage 6B.9F — period guard fetch. Used by handlePost to block
  // posting into a locked / closed / missing period.
  const [periods, setPeriods] = useState([]);
  useEffect(() => {
    let cancelled = false;
    loadPeriodsForGuard(accountsApi).then((out) => {
      if (cancelled) return;
      setPeriods((out && out.rows) || []);
    });
    return () => { cancelled = true; };
  }, []);

  const openAdd = useCallback(() => {
    setDrawerMode('add'); setDrawerRecord(null); setDrawerOpen(true);
  }, []);
  const openWithFetch = useCallback(async (record, mode) => {
    try {
      const full = await paymentVoucherApi.get(record.id);
      setDrawerMode(mode);
      setDrawerRecord(full);
      setDrawerOpen(true);
    } catch (e) {
      message.error(e?.response?.data?.error?.message || 'Failed to load payment.');
    }
  }, []);
  const openEdit = useCallback((record) => openWithFetch(record, 'edit'), [openWithFetch]);
  const openView = useCallback((record) => openWithFetch(record, 'view'), [openWithFetch]);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  const onSaved = useCallback(async () => {
    setDrawerOpen(false);
    await refetch();
  }, [refetch]);

  const handlePost = useCallback(async (record) => {
    if (record.status !== 'draft') {
      message.warning(`Only draft ${record.paymentType}s can be posted. This record is already ${record.status}.`);
      return;
    }
    // Stage 6B.9F — period guard (client-side pre-check).
    const cls = classifyPeriodForDate(periods, record.paymentDate);
    const guard = periodGuardError(cls);
    if (guard) {
      message.error(guard.message);
      return;
    }
    const isReceipt = record.paymentType === 'receipt';
    const confirmed = await new Promise((resolve) => {
      let okClicked = false;
      Modal.confirm({
        title: `Post ${isReceipt ? 'receipt' : 'payment'} ${record.paymentNo}?`,
        icon: <CheckCircleOutlined style={{ color: '#3f8600' }} />,
        content: `Posting will update ledger balances and lock the row. You will be asked for a 6-digit Approval MFA code to confirm.`,
        okText: 'Post',
        cancelText: 'Cancel',
        onOk:    () => { okClicked = true; },
        onCancel: () => { okClicked = false; },
        afterClose: () => resolve(okClicked)
      });
    });
    if (!confirmed) return;
    try {
      await runWithApproval({
        operationKey:   isReceipt ? 'accounts.receipt.post' : 'accounts.payment.post',
        resourceType:   'payment',
        resourceId:     record.id,
        operationLabel: isReceipt ? 'Post customer receipt' : 'Post supplier payment',
        resourceLabel:  `${record.paymentNo} · ₹${Number(record.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        action: (approvalMfaChallengeId) =>
          paymentVoucherApi.post(record.id, { approvalMfaChallengeId })
      });
      message.success(isReceipt ? 'Receipt posted.' : 'Payment posted.');
      await refetch();
    } catch (err) {
      if (err && err.name === 'ApprovalMfaCancelled') return;
      message.error(err?.response?.data?.error?.message ||
        `Could not post the ${isReceipt ? 'receipt' : 'payment'}. Please check the form and try again.`);
    }
  }, [refetch, runWithApproval, periods]);

  const handleCancel = useCallback(async (record) => {
    if (record.status !== 'posted') {
      message.warning(`Only posted ${record.paymentType}s can be cancelled (this one is ${record.status}).`);
      return;
    }
    const isReceipt = record.paymentType === 'receipt';
    const reason = await new Promise((resolve) => {
      let value = '';
      let okClicked = false;
      const ReasonForm = () => (
        <div>
          <p style={{ marginTop: 0 }}>
            Cancelling will REVERSE the ledger impact of this posted {isReceipt ? 'receipt' : 'payment'}.
            Provide a short reason (audit trail).
          </p>
          <Form layout="vertical">
            <Form.Item label="Reason" required>
              <Input.TextArea rows={3} maxLength={500}
                onChange={(e) => { value = e.target.value; }} />
            </Form.Item>
          </Form>
        </div>
      );
      Modal.confirm({
        title: `Cancel ${isReceipt ? 'receipt' : 'payment'} ${record.paymentNo}?`,
        icon: <ExclamationCircleOutlined />,
        content: <ReasonForm />,
        okText: 'Continue',
        okButtonProps: { danger: true },
        cancelText: 'Keep',
        onOk:    () => { okClicked = true; },
        onCancel: () => { okClicked = false; },
        afterClose: () => resolve(okClicked ? value : null)
      });
    });
    if (!reason) return;
    if (String(reason).trim().length < 3) {
      message.error('Reason must be at least 3 characters.');
      return;
    }
    try {
      await runWithApproval({
        operationKey:   isReceipt ? 'accounts.receipt.cancel' : 'accounts.payment.cancel',
        resourceType:   'payment',
        resourceId:     record.id,
        operationLabel: isReceipt ? 'Cancel customer receipt' : 'Cancel supplier payment',
        resourceLabel:  `${record.paymentNo}`,
        action: (approvalMfaChallengeId) =>
          paymentVoucherApi.cancel(record.id, { reason: String(reason).trim() }, { approvalMfaChallengeId })
      });
      message.success(isReceipt ? 'Receipt cancelled.' : 'Payment cancelled.');
      await refetch();
    } catch (err) {
      if (err && err.name === 'ApprovalMfaCancelled') return;
      message.error(err?.response?.data?.error?.message ||
        `Failed to cancel ${isReceipt ? 'receipt' : 'payment'}.`);
    }
  }, [refetch, runWithApproval]);

  const handleDelete = useCallback(async (record) => {
    if (record.status !== 'draft') {
      message.warning(`Only draft ${record.paymentType}s can be deleted. This record is already ${record.status} — use Cancel instead.`);
      return;
    }
    const isReceipt = record.paymentType === 'receipt';
    const confirmed = await new Promise((resolve) => {
      let okClicked = false;
      Modal.confirm({
        title: `Delete draft ${record.paymentNo}?`,
        icon:  <ExclamationCircleOutlined />,
        content: `The draft ${isReceipt ? 'receipt' : 'payment'} will be soft-deleted. You will be asked for a 6-digit Approval MFA code to confirm.`,
        okText: 'Delete', okButtonProps: { danger: true }, cancelText: 'Keep',
        onOk:    () => { okClicked = true; },
        onCancel: () => { okClicked = false; },
        afterClose: () => resolve(okClicked)
      });
    });
    if (!confirmed) return;
    try {
      // Stage 6B.9F — Approval-MFA L1 for draft delete. The op key is
      // resolved server-side from payment_type (PAYMENT_DELETE vs
      // RECEIPT_DELETE) so the audit row reads correctly; we pass
      // the operator-facing key here so the MFA dialog title matches.
      await runWithApproval({
        operationKey:   isReceipt ? 'accounts.receipt.delete' : 'accounts.payment.delete',
        resourceType:   'payment',
        resourceId:     record.id,
        operationLabel: isReceipt ? 'Delete draft customer receipt' : 'Delete draft supplier payment',
        resourceLabel:  `${record.paymentNo}`,
        action: (approvalMfaChallengeId) =>
          paymentVoucherApi.remove(record.id, { approvalMfaChallengeId })
      });
      message.success('Draft deleted.');
      await refetch();
    } catch (e) {
      if (e && e.name === 'ApprovalMfaCancelled') return;
      message.error(e?.response?.data?.error?.message || 'Could not delete the draft. Please refresh and try again.');
    }
  }, [refetch, runWithApproval]);

  const actionsProp     = useMemo(() => ({ add: true, view: true, edit: true, delete: false }), []);
  const pageSizeOptions = useMemo(() => ['15', '25', '50', '100'], []);

  const contextMenuActions = useMemo(() => ([
    { key: 'view',   label: 'View',         icon: <EyeOutlined />,            onClick: openView },
    { key: 'edit',   label: 'Edit Draft',   icon: <EditOutlined />,           onClick: openEdit },
    { key: 'post',   label: 'Post',         icon: <CheckCircleOutlined />,    onClick: handlePost },
    { key: 'cancel', label: 'Cancel',       icon: <CloseCircleOutlined />,    danger: true, onClick: handleCancel },
    { key: 'delete', label: 'Delete Draft', icon: <DeleteOutlined />,         danger: true, onClick: handleDelete }
  ]), [openView, openEdit, handlePost, handleCancel, handleDelete]);

  const toolbarExtra = useMemo(() => (
    <Space size="small">
      <span style={{ fontSize: 12, color: '#666' }}>Type:</span>
      <Select
        size="small" style={{ width: 160 }}
        value={paymentTypeFilter}
        onChange={(v) => setPaymentTypeFilter(v)}
        options={[
          { value: 'all',     label: 'All payments + receipts' },
          ...PAYMENT_TYPES.map((t) => ({ value: t.value, label: PAYMENT_TYPE_LABEL[t.value] }))
        ]}
      />
    </Space>
  ), [paymentTypeFilter]);

  return (
    <ErpMasterShell>
      <div className="erp-accounts-page">
      <ErpDataGrid
        title="Payment / Receipt Vouchers"
        gridId="accounts-payment-vouchers-v1"
        columns={columns}
        rowKey="id"
        rowHeight="compact"
        stickyHeader
        selectable
        actionWidth={56}
        pageSizeOptions={pageSizeOptions}
        searchable
        searchPlaceholder="Search by voucher no / reference / remarks…"
        searchShortcut
        exportable
        exportFilename="payments-receipts"
        actions={actionsProp}
        addButtonText="Add Payment / Receipt"
        emptyTitle="No payment or receipt yet"
        emptyText="Click Add Payment / Receipt to record your first money-movement entry."
        emptyCtaLabel="Add Payment / Receipt"
        onAdd={openAdd}
        onEdit={openEdit}
        onView={openView}
        onRowClick={openView}
        contextMenuActions={contextMenuActions}
        toolbarExtra={toolbarExtra}
        {...gridHandlers}
      />
      <PaymentVoucherModal
        open={drawerOpen}
        mode={drawerMode}
        record={drawerRecord}
        onClose={closeDrawer}
        onSaved={onSaved}
      />
      {ApprovalMfaModalSlot}
      </div>
    </ErpMasterShell>
  );
}
